import React, { useState } from 'react';
import { Phone, Mail, Share2, MapPin } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { getImageUrl, buildResponsiveImage } from '../lib/imageUtils';
import { trackLinkClick } from '../lib/analytics';
import { getStallShareUrl } from '../lib/slug';

const StallCard = ({ stall, onClick, selectedMarket, onGetDirections, eager = false }) => {
  // State for image errors only
  const [bannerError, setBannerError] = useState(false);
  const [logoError, setLogoError] = useState(false);

  // Defensive onError: if a srcset variant 404s, strip the srcset and retry the
  // main src. Only flag as fully errored once the main URL has also failed.
  const handleImgError = (setError) => (e) => {
    const img = e.currentTarget;
    if (img.getAttribute('srcset')) {
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      // Force a reload from the bare src
      const original = img.src;
      img.src = '';
      img.src = original;
      return;
    }
    setError(true);
  };

  // Get location based on selected market filter or default to primary location
  const getLocationForMarket = () => {
    if (selectedMarket && selectedMarket !== 'All Markets' && stall.locations?.length > 0) {
      const marketLoc = stall.locations.find(loc => loc.market === selectedMarket);
      if (marketLoc && (marketLoc.lat || marketLoc.lng)) {
        return { 
          lat: marketLoc.lat, 
          lng: marketLoc.lng, 
          stallNumber: marketLoc.stallNumber || stall.stallNumber,
          market: marketLoc.market
        };
      }
    }
    
    if (stall.locations?.length > 0) {
      const locWithCoords = stall.locations.find(loc => loc.lat && loc.lng);
      if (locWithCoords) {
        return { 
          lat: locWithCoords.lat, 
          lng: locWithCoords.lng, 
          stallNumber: locWithCoords.stallNumber || stall.stallNumber,
          market: locWithCoords.market
        };
      }
    }
    
    return { 
      lat: stall.location?.lat, 
      lng: stall.location?.lng, 
      stallNumber: stall.stallNumber,
      market: stall.market
    };
  };

  const currentLocation = getLocationForMarket();
  const hasLocation = currentLocation.lat && currentLocation.lng;
  const displayStallNumber = currentLocation.stallNumber || stall.stallNumber;

  const handleGetDirections = (e) => {
    e.stopPropagation();
    if (!hasLocation) {
      alert('Location not available for this stall');
      return;
    }
    trackLinkClick('directions', 'directions', stall._id, stall.stallName, currentLocation.market || stall.market);
    if (onGetDirections) {
      onGetDirections(stall, currentLocation.lat, currentLocation.lng, currentLocation.market || stall.market);
    }
  };

  const handleCall = (e) => {
    e.stopPropagation();
    trackLinkClick('phone', 'contact', stall._id, stall.stallName);
    window.location.href = `tel:${stall.phone}`;
  };

  const handleEmail = (e) => {
    e.stopPropagation();
    trackLinkClick('email', 'contact', stall._id, stall.stallName);
    window.location.href = `mailto:${stall.email}`;
  };

  const handleShare = (e) => {
    e.stopPropagation();
    const shareUrl = getStallShareUrl(stall);
    const shareMessage = `Check out "${stall.stallName}" on WAYO — the vendor platform.`;
    
    if (navigator.share) {
      navigator.share({
        title: `${stall.stallName} | WAYO`,
        text: shareMessage,
        url: shareUrl,
      }).catch(() => {
        // If share fails, copy URL and message
        navigator.clipboard.writeText(shareUrl);
        alert('Link copied to clipboard!');
      });
    } else {
      // Copy just the URL - the OG tags will provide the preview text
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    }
  };

  // Get image URLs (with responsive srcset)
  const banner = stall.banner
    ? buildResponsiveImage(stall.banner, { fallbackWidth: 1920 })
    : { src: null, srcSet: null };
  const logo = stall.logo
    ? buildResponsiveImage(stall.logo, { fallbackWidth: 800 })
    : { src: null, srcSet: null };
  const bannerUrl = banner.src;
  const logoUrl = logo.src;

  return (
    <Card 
      className="overflow-hidden cursor-pointer group hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 bg-white dark:bg-[#1a1a1a] border-2 border-gray-100 dark:border-gray-800 w-full"
      onClick={onClick}
    >
      <div className="relative h-32 sm:h-40 md:h-48 overflow-hidden bg-gray-200 dark:bg-gray-800">
        {/* Banner Image */}
        {bannerUrl && !bannerError ? (
          <img 
            src={bannerUrl}
            srcSet={banner.srcSet || undefined}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            alt={stall.stallName}
            className="w-full h-full object-cover group-hover:scale-110 transition-all duration-700"
            loading={eager ? "eager" : "lazy"}
            decoding="async"
            fetchPriority={eager ? "high" : "auto"}
            onError={handleImgError(setBannerError)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800">
            <span className="text-gray-400 dark:text-gray-500 text-sm">
              {bannerError ? 'Image unavailable' : 'No Banner'}
            </span>
          </div>
        )}
        
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        
        {/* Logo */}
        <div className="absolute bottom-2 left-2 sm:bottom-4 sm:left-4">
          <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full overflow-hidden border-2 sm:border-4 border-white shadow-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
            {logoUrl && !logoError ? (
              <img 
                src={logoUrl}
                srcSet={logo.srcSet || undefined}
                sizes="64px"
                alt={`${stall.stallName} logo`}
                className="w-full h-full object-cover"
                loading={eager ? "eager" : "lazy"}
                decoding="async"
                onError={handleImgError(setLogoError)}
              />
            ) : (
              <span className="text-gray-400 text-xs">{logoError ? '!' : 'Logo'}</span>
            )}
          </div>
        </div>
      </div>
      
      <CardContent className="p-3 sm:p-4">
        <div className="mb-2 sm:mb-3 overflow-hidden">
          <h3 className="text-sm sm:text-lg font-bold text-gray-900 dark:text-white mb-0.5 sm:mb-1 group-hover:text-[#FFD12D] transition-colors duration-300 line-clamp-1 break-words">
            {stall.stallName}
          </h3>
          <div className="flex items-center gap-1 sm:gap-2 text-xs text-gray-600 dark:text-gray-400">
            <MapPin className="w-3 h-3 text-[#FFD12D] flex-shrink-0" />
            <span className="font-semibold truncate">Stall {displayStallNumber}</span>
            {!hasLocation && (
              <span className="text-red-400 text-[10px] flex-shrink-0">(No GPS)</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 sm:gap-1.5 overflow-hidden">
          <Button
            onClick={handleGetDirections}
            size="sm"
            disabled={!hasLocation}
            className={`flex-1 font-semibold transition-all duration-300 text-[10px] sm:text-xs h-7 sm:h-8 px-1.5 sm:px-2 min-w-0 ${
              hasLocation 
                ? 'bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] border-[#FFD12D]' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Directions
          </Button>
          <Button
            onClick={handleCall}
            size="icon"
            variant="ghost"
            className="h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 rounded-full hover:bg-[#FFD12D]/20 hover:text-[#252525] transition-all duration-300"
          >
            <Phone className="w-3 h-3 sm:w-4 sm:h-4" />
          </Button>
          <Button
            onClick={handleEmail}
            size="icon"
            variant="ghost"
            className="h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 rounded-full hover:bg-[#FFD12D]/20 hover:text-[#252525] transition-all duration-300"
          >
            <Mail className="w-3 h-3 sm:w-4 sm:h-4" />
          </Button>
          <Button
            onClick={handleShare}
            size="icon"
            variant="ghost"
            className="h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 rounded-full hover:bg-[#FFD12D]/20 hover:text-[#252525] transition-all duration-300"
          >
            <Share2 className="w-3 h-3 sm:w-4 sm:h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default StallCard;
